/**
 * translate.ts — course translation engine (Phase 1: text + narration, no audio).
 *
 * A translation is a NEW child course (is_root:false, root_course_id, target
 * language, fresh module/slide IDs). We never re-author content — we translate
 * only natural-language strings and keep everything structural untouched.
 *
 * Strategy: extract → translate → re-inject BY PATH.
 *   - extractStrings() walks slide content collecting only human-readable string
 *     leaves, skipping URLs/assets/colors/enums/numbers and any `_`-prefixed
 *     flag key (except `_narration`, which IS translated).
 *   - translateBatch() sends those strings as a JSON array to Claude and gets a
 *     same-order array back, with the "keep technical/brand terms in English"
 *     rule.
 *   - reinjectStrings() deep-clones the content and sets each translated value
 *     back at its exact path. Structure, URLs, colors, and correctIndex are
 *     provably preserved — a mistranslated quiz index silently breaks the quiz,
 *     so we never hand the whole JSON to the model.
 */

import { streamMessage } from './aiProvider'
import { getCourse, getCourseModules, createTranslatedCourse, saveCourseModules, deleteCourse } from './db'
import { getLanguage, type VoiceGender } from './languages'
import { generateAudioB64, friendlyTtsError } from './tts'
import { saveAudio, getSlideAudioSet, storageAudioName, audioFormatFor } from './audioCache'
import { narrationHash } from './audioHash'
import type { Course, Module } from '@/types'

/**
 * Does a course actually have voiceover audio cached (in this browser)? Used to
 * decide whether the translated copy should regenerate voiceover at all — we
 * only voice a translation if the original was voiced. Short-circuits on the
 * first hit. (Audio is device-local, so this reflects the creator's device.)
 */
export async function courseHasVoiceover(courseId: string, modules: Module[], language = 'en'): Promise<boolean> {
  for (const m of modules) {
    for (const s of (m.slides ?? [])) {
      const segs = Array.isArray((s.content as { _narration?: unknown })._narration)
        ? ((s.content as { _narration?: unknown })._narration as string[]) : []
      if (!segs.length) continue
      const stored = await getSlideAudioSet(courseId, s.id)
      for (let i = 0; i < segs.length; i++) {
        if (stored.has(storageAudioName(i, 'female', narrationHash(segs[i] ?? ''), audioFormatFor(language, 'female')))) return true
        if (stored.has(storageAudioName(i, 'male', narrationHash(segs[i] ?? ''), audioFormatFor(language, 'male')))) return true
      }
    }
  }
  return false
}

/**
 * Narration coverage for a course: total non-empty narration segments and how
 * many already have voiceover audio cached (this device). Used for the
 * "X / Y narration" line on the library-detail cards.
 */
export async function courseVoiceoverStats(
  courseId: string, modules: Module[], language = 'en',
): Promise<{ total: number; ready: number; slidesTotal: number; slidesReady: number }> {
  // Segments, and slides (a slide is ready when every one of its segments has a voiceover).
  // One storage listing per narrated slide — all at once, not one after another (9 Oct 2026:
  // in sequence a 16-slide course spent over two seconds here before the course page showed).
  const narrated = modules.flatMap(m => m.slides ?? []).map(s => ({ s, segs: Array.isArray((s.content as { _narration?: unknown })._narration)
    ? ((s.content as { _narration?: unknown })._narration as string[]) : [] })).filter(x => x.segs.length)
  const sets = await Promise.all(narrated.map(x => getSlideAudioSet(courseId, x.s.id)))
  let total = 0, ready = 0, slidesTotal = 0, slidesReady = 0
  for (const [k, { segs }] of narrated.entries()) {
    const stored = sets[k]
    let mine = 0, mineReady = 0
    for (let i = 0; i < segs.length; i++) {
      if (!segs[i]?.trim?.()) continue
      mine++
      if (stored.has(storageAudioName(i, 'female', narrationHash(segs[i]), audioFormatFor(language, 'female'))) ||
          stored.has(storageAudioName(i, 'male', narrationHash(segs[i]), audioFormatFor(language, 'male')))) mineReady++
    }
    total += mine; ready += mineReady
    if (mine) { slidesTotal++; if (mineReady === mine) slidesReady++ }
  }
  return { total, ready, slidesTotal, slidesReady }
}

// ── Skip rules ────────────────────────────────────────────────────────────────

/**
 * Keys whose entire value/subtree must never be translated. Derived from the
 * format registry: every `color` / `select` (enum) / `image` / `video` / `pdf`
 * field, plus icon/asset/style keys. `correctAnswer` ('true'|'false') and
 * `focalPoint` ('top'|'centre'|'bottom') are enums — translating them breaks
 * the slide. Text fields (title, heading, statement, verdict, hint, summary,
 * answerText, motivationMessage, …) are deliberately NOT here.
 */
const SKIP_KEYS = new Set<string>([
  // colours
  'accentColor', 'accentColour', 'answerBg', 'answerColor', 'background',
  'backgroundImage', 'fontColor', 'leftColor', 'rightColor', 'panelColor',
  'bgColor', 'color', 'defaultColor',
  // assets / urls
  'image', 'backgroundVideo', 'pdf', 'logo', 'imageLinkUrl', 'videoUrl',
  'videoId', 'photoId', 'src', 'poster', 'thumbnail', 'thumbnailUrl', 'url',
  'focalPoint',
  // enums / style
  'mode', 'imageDisplayMode', 'imageFit', 'imageSide', 'layoutVariant',
  'revealMode', 'fontSize', 'fontStyle', 'darkMode', 'defaultOpen',
  'panelOpacity', 'timerSeconds', 'correctAnswer', 'correctIndex', 'videoType',
  'variant', 'align', 'theme', 'side', 'position', 'status',
  // icons (emoji / glyph — not prose)
  'leftIcon', 'rightIcon', 'icon',
])

/** Underscore flags are internal config — skip them all EXCEPT _narration. */
function isSkippedKey(key: string): boolean {
  if (key === '_narration') return false
  if (key.startsWith('_')) return true
  return SKIP_KEYS.has(key)
}

const HEX_RE = /^#?[0-9a-fA-F]{3,8}$/
const URL_RE = /^(https?:\/\/|data:|blob:|\/)/
const ASSET_RE = /\.(webp|png|jpe?g|gif|svg|mp4|webm|mov|pdf)(\?|$)/i
const NO_LETTERS_RE = /^[\d\s.,:%/_+#-]*$/

/** A string is translatable unless it's a colour, URL, asset, or has no letters. */
function isTranslatableString(s: string): boolean {
  const t = s.trim()
  if (!t) return false
  if (HEX_RE.test(t)) return false
  if (URL_RE.test(t)) return false
  if (ASSET_RE.test(t)) return false
  if (NO_LETTERS_RE.test(t)) return false
  return true
}

// ── Extract / reinject ──────────────────────────────────────────────────────

export interface ExtractedString {
  path: (string | number)[]
  text: string
}

/** Recursively collect translatable string leaves with their paths. */
export function extractStrings(
  value: unknown,
  path: (string | number)[] = [],
  out: ExtractedString[] = [],
): ExtractedString[] {
  if (typeof value === 'string') {
    if (isTranslatableString(value)) out.push({ path, text: value })
    return out
  }
  if (Array.isArray(value)) {
    value.forEach((v, i) => extractStrings(v, [...path, i], out))
    return out
  }
  if (value && typeof value === 'object') {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (isSkippedKey(k)) continue
      extractStrings(v, [...path, k], out)
    }
  }
  return out
}

function setAtPath(root: unknown, path: (string | number)[], val: string): void {
  let cur = root as Record<string | number, unknown>
  for (let i = 0; i < path.length - 1; i++) cur = cur[path[i]] as Record<string | number, unknown>
  cur[path[path.length - 1]] = val
}

/** Deep-clone `content` and write each translated string back at its path. */
export function reinjectStrings(
  content: Record<string, unknown>,
  extracted: ExtractedString[],
  translated: string[],
): Record<string, unknown> {
  const clone = structuredClone(content)
  extracted.forEach((e, i) => {
    if (translated[i] != null) setAtPath(clone, e.path, translated[i])
  })
  return clone
}

// ── Model call ────────────────────────────────────────────────────────────────

function stripJsonFence(s: string): string {
  return s.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '').trim()
}

/**
 * Parse a JSON object from a model reply, tolerating stray prose/fences (models
 * sometimes prefix "I need to…" before the JSON). Tries a clean parse first,
 * then extracts the outermost {…}. Throws if no object is present.
 */
function parseJsonObject(raw: string): unknown {
  const cleaned = stripJsonFence(raw)
  try { return JSON.parse(cleaned) } catch { /* try extraction below */ }
  const start = cleaned.indexOf('{')
  const end = cleaned.lastIndexOf('}')
  if (start >= 0 && end > start) return JSON.parse(cleaned.slice(start, end + 1))
  throw new SyntaxError('no JSON object found in model reply')
}

/** How many chunk-translations / TTS calls run at once. Both call sites
 *  already retry 429/5xx with backoff (withModelRetry / withTtsRetry), so a
 *  handful of parallel requests should self-heal into rate limits rather than
 *  fail outright — kept modest (not e.g. 10+) to stay polite to both APIs. */
const CONCURRENCY = 4

/**
 * Run `fn` over `items` with at most `limit` in flight at once. Results land
 * at their ORIGINAL index regardless of completion order, so callers that
 * depend on positional order (e.g. translateBatch's chunk → string mapping)
 * stay correct under concurrency.
 */
async function mapWithConcurrency<T, R>(
  items: T[], limit: number, fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length)
  let next = 0
  async function worker(): Promise<void> {
    for (;;) {
      const i = next++
      if (i >= items.length) return
      results[i] = await fn(items[i], i)
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker))
  return results
}

/**
 * Retry a model (Claude proxy) call on gateway timeouts (504), other 5xx, and
 * rate limits (429) with exponential backoff + jitter. Non-Latin translations
 * produce large/slow responses that can time out the serverless proxy → 504;
 * these are usually transient, so a retry self-heals instead of failing the
 * whole job. User-aborts and non-retryable errors propagate immediately.
 */
async function withModelRetry<T>(fn: () => Promise<T>, signal?: AbortSignal, maxAttempts = 3): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await fn()
    } catch (err) {
      if (signal?.aborted) throw err
      const status = (err as { status?: number })?.status
      const msg = err instanceof Error ? err.message : String(err)
      const retryable =
        status === 429 || (typeof status === 'number' && status >= 500) ||
        /\b(429|5\d\d)\b/.test(msg) ||
        /timeout|timed out|overloaded|ETIMEDOUT|fetch failed|network error/i.test(msg)
      if (!retryable || attempt >= maxAttempts) throw err
      const delay = Math.min(15000, 2000 * 2 ** (attempt - 1)) + Math.random() * 500
      console.warn(`[translate] model call failed (${status ?? msg.slice(0, 40)}) — retry ${attempt}/${maxAttempts - 1} in ${Math.round(delay)}ms`)
      await new Promise<void>((resolve, reject) => {
        const t = setTimeout(resolve, delay)
        signal?.addEventListener('abort', () => { clearTimeout(t); reject(new DOMException('aborted', 'AbortError')) }, { once: true })
      })
    }
  }
}

/** Map a raw translation error to a friendly, user-facing message. */
export function friendlyTranslateError(err: unknown): string {
  const status = (err as { status?: number })?.status
  const msg = err instanceof Error ? err.message : String(err)
  // A translation job runs many TTS calls for the voiceover step; surface the
  // out-of-credits case with the same wording used elsewhere in the app.
  const ttsMsg = friendlyTtsError(err)
  if (ttsMsg) return ttsMsg
  if (/translated slides couldn’t be saved/.test(msg)) return msg
  if (status === 504 || /\b504\b|timeout|timed out|ETIMEDOUT/i.test(msg))
    return 'The translation timed out — this can happen with longer courses. Please try again.'
  if (status === 429 || /\b429\b|rate limit|overloaded|\b529\b/i.test(msg))
    return 'The translation service is busy right now. Please wait a moment and try again.'
  if ((typeof status === 'number' && status >= 500) || /\b5\d\d\b|fetch failed|network/i.test(msg))
    return 'The translation service had a temporary error. Please try again.'
  return 'Translation failed. Please try again.'
}

/**
 * Delimiter between batch items. A JSON array of free text is fragile — the
 * model routinely emits unescaped quotes / em-dashes inside values (German „…",
 * etc.), which breaks JSON.parse mid-array. So we join items with this rare
 * sentinel and ask for them back the same way: no escaping, so quotes/newlines
 * in the text can't corrupt parsing. Validate the count, else keep originals.
 */
const BATCH_SEP = '@@@~LB~SEP~@@@'

// Non-Latin scripts cost 2–3× tokens (gotcha mirrored from ai.ts), so their
// responses are bigger/slower and must be chunked tighter to avoid 504s.
const NON_LATIN_SCRIPTS = new Set(['hi', 'mr', 'gu', 'bn', 'pa', 'ta', 'kn', 'te', 'ml', 'ja'])

/**
 * Translate ONE chunk (a single model call). Returns a same-length array; on a
 * parse/count failure it keeps the chunk's originals (never throws on bad
 * output — only re-throws abort / API errors).
 */
async function translateChunk(
  chunk: string[], sourceLabel: string, targetLabel: string, targetCode: string, mult: number, signal?: AbortSignal,
): Promise<string[]> {
  const chars = chunk.reduce((n, s) => n + s.length, 0)
  const maxTokens = Math.min(8000, Math.max(800, chars * 2 * mult))

  const system =
    `You are a professional localisation engine translating UI/course strings ` +
    `from ${sourceLabel} to ${targetLabel}.\n` +
    `The input has ${chunk.length} item(s), each separated by a line containing ` +
    `exactly:\n${BATCH_SEP}\n\n` +
    `RULES:\n` +
    `- Return ONLY the ${chunk.length} translations, in the SAME order, separated ` +
    `the SAME way — a line containing exactly ${BATCH_SEP} between each.\n` +
    `- Output EXACTLY ${chunk.length} items. Never merge, split, drop, reorder, ` +
    `number, or add any commentary, quotes, or code fences.\n` +
    `- Translate naturally and idiomatically for a learner audience.\n` +
    `- KEEP unchanged: brand names, product names, proper nouns, and widely-used ` +
    `industry/technical terms normally written in English.\n` +
    `- Preserve HTML tags, markdown, emoji, numbers, and {placeholders} exactly. ` +
    `An empty item stays empty.`

  let raw: string
  try {
    // STREAM the response: the Anthropic proxy is an Edge function that blocks
    // on a non-streaming call until the full (slow, non-Latin) response is
    // ready → 25s timeout → 504. Streaming flows SSE through immediately so the
    // function never blocks. We ignore chunks and use the returned full text.
    raw = await withModelRetry(() => streamMessage({
      system,
      messages: [{ role: 'user', content: chunk.join(`\n${BATCH_SEP}\n`) }],
      max_tokens: maxTokens,
      onChunk: () => {},
    }), signal)
  } catch (err) {
    console.error('[translate] model call failed (after retries)', {
      sourceLabel, targetLabel, targetCode, count: chunk.length, chars, maxTokens,
      message: err instanceof Error ? err.message : String(err), error: err,
    })
    throw err // propagates → job fails
  }

  const cleaned = raw.replace(/```[a-z]*\n?/gi, '').replace(/```/g, '')
  const parts = cleaned.split(BATCH_SEP).map(s => s.replace(/^\s*\n|\n\s*$/g, '').trim())
  if (parts.length === chunk.length) {
    return parts.map((p, i) => (p.length > 0 ? p : chunk[i])) // keep originals for blanks
  }
  console.error('[translate] chunk item-count mismatch, keeping originals', {
    expected: chunk.length, got: parts.length, rawPreview: raw.slice(0, 400),
  })
  return chunk
}

/**
 * Translate an ordered array of strings. Splits into small character-budgeted
 * chunks so each model call stays well under the serverless proxy's timeout
 * (big non-Latin responses on text-heavy slides were 504-ing). Returns a
 * same-length array; failures fall back to originals per chunk (never throws on
 * bad output).
 */
export async function translateBatch(
  strings: string[],
  sourceLabel: string,
  targetLabel: string,
  targetCode: string,
  signal?: AbortSignal,
): Promise<string[]> {
  if (strings.length === 0) return []
  if (signal?.aborted) throw new DOMException('aborted', 'AbortError')

  const mult = NON_LATIN_SCRIPTS.has(targetCode.split('-')[0]) ? 3 : 1
  const charBudget = mult > 1 ? 1200 : 3000 // tighter for non-Latin (3× output)

  // Greedy chunking by source-char budget; every chunk has ≥1 string (a single
  // oversized string still becomes its own — far smaller than a whole slide).
  const chunks: string[][] = []
  let cur: string[] = []
  let curChars = 0
  for (const s of strings) {
    if (cur.length > 0 && curChars + s.length > charBudget) { chunks.push(cur); cur = []; curChars = 0 }
    cur.push(s); curChars += s.length
  }
  if (cur.length) chunks.push(cur)

  const chunkResults = await mapWithConcurrency(chunks, CONCURRENCY, chunk =>
    translateChunk(chunk, sourceLabel, targetLabel, targetCode, mult, signal))
  return chunkResults.flat()
}

// ── fill-blanks (format-aware) ────────────────────────────────────────────────
//
// fill-blanks is index-based: `blanks: [{index, word}]` where `index` is the
// WORD position in the space-split `statement`, and the component self-heals the
// answer from statement.split(' ')[index]. Translating the statement reorders
// and recounts words, so the generic per-string walker would corrupt every
// blank. Here we translate the statement AND ask the model to re-point each
// blank to the matching word index in the translation, then validate. If
// validation fails (e.g. a space-less language like Japanese, or a count
// mismatch), we keep the exercise body in the SOURCE language so it still works
// — only the title + narration get translated.

interface Blank { index: number; word: string }

export async function translateFillBlanks(
  content: Record<string, unknown>,
  sourceLabel: string,
  targetLabel: string,
  targetCode: string,
  signal?: AbortSignal,
): Promise<Record<string, unknown>> {
  const clone = structuredClone(content)

  // 1) Title + narration via the generic batch (safe — not index-coupled).
  const safe = extractStrings(clone).filter(
    e => e.path[0] === 'title' || e.path[0] === '_narration',
  )
  if (safe.length > 0) {
    const t = await translateBatch(safe.map(e => e.text), sourceLabel, targetLabel, targetCode, signal)
    safe.forEach((e, i) => { if (t[i] != null) setAtPath(clone, e.path, t[i]) })
  }

  const statement = typeof clone.statement === 'string' ? clone.statement : ''
  const blanks = Array.isArray(clone.blanks) ? (clone.blanks as Blank[]) : []
  const distractors = Array.isArray(clone.additionalWords) ? (clone.additionalWords as string[]) : []
  if (!statement.trim() || blanks.length === 0) return clone

  const srcWords = statement.split(' ')
  const blankedWords = blanks.map(b => ({
    sourceIndex: b.index,
    word: (srcWords[b.index] ?? b.word ?? '').replace(/[.,!?;:]+$/, ''),
  }))

  const system =
    `You are a localisation engine for a fill-in-the-blank exercise. Translate ` +
    `from ${sourceLabel} to ${targetLabel}. Keep brand/technical terms in English.\n` +
    `CRITICAL: Output ONLY the raw JSON object — no preamble, no explanation ` +
    `(never begin with "I need to" or similar), no markdown fences. Your reply ` +
    `must start with { and end with }.`
  const user = JSON.stringify({
    statement,
    blankedWords,
    distractors,
    instructions:
      'Translate `statement`. In the translated statement, blanked words must ' +
      'each be a single space-delimited token. Return {"statement": "<translated, ' +
      'space-separated words>", "blanks": [{"index": <0-based word index of the ' +
      'translated blanked word in the translated statement>, "word": "<that ' +
      'word>"}], "additionalWords": [<translated distractors>]}. Keep the same ' +
      'number of blanks, one per input blankedWord, in the same order.',
  })

  try {
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError')
    const raw = await withModelRetry(() => streamMessage({
      system, messages: [{ role: 'user', content: user }],
      max_tokens: Math.min(4000, Math.max(1500, statement.length * 4)),
      onChunk: () => {},
    }), signal)
    const parsed = parseJsonObject(raw) as {
      statement?: string; blanks?: Blank[]; additionalWords?: string[]
    }
    const newStatement = (parsed.statement ?? '').trim()
    const newBlanks = parsed.blanks ?? []
    const newWords = newStatement.split(' ')

    const valid =
      newStatement.length > 0 &&
      newBlanks.length === blanks.length &&
      newBlanks.every(b =>
        Number.isInteger(b.index) && b.index >= 0 && b.index < newWords.length)

    if (valid) {
      clone.statement = newStatement
      // Trust the translated statement as the source of truth for each word
      // (mirrors the component's self-healing) — store the cleaned token.
      clone.blanks = newBlanks.map(b => ({
        index: b.index,
        word: (newWords[b.index] ?? b.word).replace(/[.,!?;:]+$/, ''),
      }))
      if (Array.isArray(parsed.additionalWords) && parsed.additionalWords.length === distractors.length) {
        clone.additionalWords = parsed.additionalWords
      }
      return clone
    }
    console.warn('[translate] fill-blanks validation failed — keeping body in source language')
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    console.warn('[translate] fill-blanks translation error — keeping body in source language', err)
  }
  // Fallback: statement / blanks / additionalWords stay in the source language.
  return clone
}

// ── Orchestration ─────────────────────────────────────────────────────────────

export type TranslateStep =
  | 'reading' | 'content' | 'preserving' | 'narration' | 'saving' | 'voiceover' | 'done'

export interface TranslateProgress {
  step: TranslateStep
  /** 0–100 */
  progress: number
}

function uid(prefix: string, i: number): string {
  return `${prefix}-${Date.now().toString(36)}-${i}-${Math.random().toString(36).slice(2, 7)}`
}

/**
 * Retry a TTS call on rate-limit (429) / server (5xx) errors with exponential
 * backoff + jitter. The TTS fetch helpers throw `… TTS <status>: …`, so we read
 * the status from the message. User-aborts and non-retryable errors propagate
 * immediately (the caller skips that segment). Anthropic calls don't need this —
 * the Anthropic SDK already retries 429/5xx internally.
 */
async function withTtsRetry<T>(fn: () => Promise<T>, signal?: AbortSignal, maxAttempts = 4): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await fn()
    } catch (err) {
      if (signal?.aborted) throw err
      const msg = err instanceof Error ? err.message : String(err)
      const status = Number(/TTS (\d{3})/.exec(msg)?.[1] ?? 0)
      const retryable = status === 429 || status >= 500
      if (!retryable || attempt >= maxAttempts) throw err
      const delay = Math.min(8000, 1000 * 2 ** (attempt - 1)) + Math.random() * 300
      await new Promise<void>((resolve, reject) => {
        const t = setTimeout(resolve, delay)
        signal?.addEventListener('abort', () => { clearTimeout(t); reject(new DOMException('aborted', 'AbortError')) }, { once: true })
      })
    }
  }
}

/**
 * Translate a course into `targetCode`, persist a new child course, and return
 * it. `onProgress` is called as work advances. Throws on AbortError or if the
 * source course can't be loaded.
 */
export async function translateCourse(
  courseId: string,
  targetCode: string,
  createdBy: string,
  onProgress: (p: TranslateProgress) => void,
  signal?: AbortSignal,
): Promise<Course> {
  const checkAbort = () => { if (signal?.aborted) throw new DOMException('aborted', 'AbortError') }

  onProgress({ step: 'reading', progress: 4 })
  const source = await getCourse(courseId)
  if (!source) throw new Error('Source course not found')
  const modules = await getCourseModules(courseId)

  const sourceLabel = getLanguage(source.language).label
  const targetLabel = getLanguage(targetCode).label

  // 1) Course title + description (one small batch). Only translatable fields
  //    are sent; map results back by their position in that filtered list.
  onProgress({ step: 'content', progress: 8 })
  const titleT = isTranslatableString(source.title ?? '')
  const descT = isTranslatableString(source.description ?? '')
  const courseStrings = [
    ...(titleT ? [source.title as string] : []),
    ...(descT ? [source.description as string] : []),
  ]
  const courseR = await translateBatch(courseStrings, sourceLabel, targetLabel, targetCode, signal)
  const newTitle = titleT ? courseR[0] : source.title
  const newDescription = descT ? courseR[titleT ? 1 : 0] : source.description

  // 2) Module titles (one batch).
  checkAbort()
  const moduleTitles = modules.map(m => m.title ?? '')
  const moduleTitlesT = await translateBatch(moduleTitles, sourceLabel, targetLabel, targetCode, signal)

  // The bulk of the work, split into two visible phases so the progress bar
  // moves through BOTH "Translating slide content" and "Generating narration
  // scripts" (narration lives in content._narration). fill-blanks is index-
  // coupled, so it's handled whole in the content phase and skipped in the
  // narration phase.
  const allSlides = modules.flatMap(m => m.slides ?? [])
  const total = Math.max(1, allSlides.length)
  const translatedContentBySlideId = new Map<string, Record<string, unknown>>()

  console.info(`[translate] ${source.language}→${targetCode} · ${allSlides.length} slides · "${source.title}"`)

  // 3) Per-slide content, EXCLUDING narration — 12→55%.
  let done = 0
  for (const m of modules) {
    for (const s of (m.slides ?? [])) {
      checkAbort()
      try {
        const content = s.content as Record<string, unknown>
        if (s.format_id === 'fill-blanks') {
          translatedContentBySlideId.set(
            s.id,
            await translateFillBlanks(content, sourceLabel, targetLabel, targetCode, signal),
          )
        } else {
          const extracted = extractStrings(content).filter(e => e.path[0] !== '_narration')
          translatedContentBySlideId.set(
            s.id,
            extracted.length > 0
              ? reinjectStrings(
                  content, extracted,
                  await translateBatch(extracted.map(e => e.text), sourceLabel, targetLabel, targetCode, signal),
                )
              : structuredClone(content),
          )
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') throw err
        console.error(`[translate] CONTENT failed on slide ${s.id} (${s.format_id})`, err)
        throw err
      }
      done++
      onProgress({ step: 'content', progress: 10 + Math.round((done / total) * 24) })
    }
  }

  // 4) Per-slide narration scripts — 55→90%.
  done = 0
  for (const m of modules) {
    for (const s of (m.slides ?? [])) {
      checkAbort()
      done++
      if (s.format_id === 'fill-blanks') {
        onProgress({ step: 'narration', progress: 36 + Math.round((done / total) * 10) })
        continue // narration already handled in translateFillBlanks
      }
      try {
        const current = translatedContentBySlideId.get(s.id) as Record<string, unknown>
        const narr = extractStrings(current).filter(e => e.path[0] === '_narration')
        if (narr.length > 0) {
          translatedContentBySlideId.set(
            s.id,
            reinjectStrings(
              current, narr,
              await translateBatch(narr.map(e => e.text), sourceLabel, targetLabel, targetCode, signal),
            ),
          )
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') throw err
        console.error(`[translate] NARRATION failed on slide ${s.id} (${s.format_id})`, err)
        throw err
      }
      onProgress({ step: 'narration', progress: 36 + Math.round((done / total) * 10) })
    }
  }

  // Build the child course id + remapped modules LOCALLY, saved in step 5.
  const rootId = source.is_root ? source.id : (source.root_course_id ?? source.id)
  const childId = `course-${Date.now()}`
  const now = new Date().toISOString()
  const defaultGender: VoiceGender = source.default_voice_gender ?? 'female'
  const newModules: Module[] = modules.map((m, mi) => {
    const newModuleId = uid('mod', mi)
    return {
      id: newModuleId,
      course_id: childId,
      title: moduleTitlesT[mi] ?? m.title,
      position: mi,
      is_opening: m.is_opening ?? false,
      created_at: now,
      slides: (m.slides ?? []).map((s, si) => ({
        id: uid('sld', mi * 1000 + si),
        module_id: newModuleId,
        format_id: s.format_id,
        position: si,
        content: translatedContentBySlideId.get(s.id) ?? s.content,
        created_at: now,
        updated_at: now,
      })),
    }
  })

  // 5) Persist the course + modules — now it appears in the library (before the voiceover, see 6).
  onProgress({ step: 'saving', progress: 47 })
  const child = await createTranslatedCourse({
    id: childId,
    rootId,
    orgId: source.org_id ?? null,
    createdBy,
    title: newTitle ?? source.title,
    description: newDescription,
    thumbnail_url: source.thumbnail_url,
    language: targetCode,
    default_voice_gender: source.default_voice_gender,
    pass_mark: source.pass_mark,
    min_questions: source.min_questions,
    default_narration_skippable: source.default_narration_skippable,
    default_auto_advance: source.default_auto_advance,
    // Every course setting the original has (12 Oct 2026) — these five used to fall back to defaults.
    default_auto_advance_segments: source.default_auto_advance_segments,
    default_interact_per_element: source.default_interact_per_element,
    font_family: source.font_family,
    narration_seconds: source.narration_seconds,
    custom_slide_theme: source.custom_slide_theme,
  })
  // The slides must really be there before this counts as done (21 Oct 2026): the save reports
  // failure by returning null, and a Hindi version was announced as ready with no slides at all.
  // On failure the empty version is removed, so nothing half-made is left on the Languages tab.
  const saved = await saveCourseModules(childId, newModules, createdBy)
  const expected = newModules.reduce((n, m) => n + (m.slides?.length ?? 0), 0)
  const stored = saved ? (await getCourseModules(childId)).reduce((n, m) => n + (m.slides?.length ?? 0), 0) : 0
  if (!saved || stored !== expected) {
    console.error('[translate] slides not saved', { childId, saved: !!saved, expected, stored })
    void deleteCourse(childId)
    throw new Error(`The translated slides couldn’t be saved (${stored} of ${expected}). Nothing was kept — please try again.`)
  }

  // Seed the editor's localStorage cache (same shape EditorPage writes) so the
  // new course loads SYNCHRONOUSLY when opened — otherwise EditorPage async-
  // fetches from Supabase and races PDF.js / YouTube hydration (half-render).
  try {
    localStorage.setItem(`learnbee_editor_${childId}`, JSON.stringify({
      title: child.title,
      language: child.language,
      modules: newModules,
      narrationSkippable: child.default_narration_skippable ?? false,
      defaultVoiceGender: child.default_voice_gender ?? 'female',
      defaultAutoAdvance: child.default_auto_advance ?? false,
      defaultAutoAdvanceSegments: child.default_auto_advance_segments ?? false,
      fontFamily: child.font_family,
      shareCode: null,
    }))
  } catch { /* quota — editor will fall back to a Supabase fetch */ }

  // 6) Voiceover — AFTER the course is saved (4 Nov 2026): /api/audio/upload checks edit rights
  //    on the course row, so before it existed every upload was refused and the voiceover lived
  //    only in this browser's IndexedDB (the editor here showed it; the course page, which reads
  //    storage, and every other device did not). A cancel now deletes the saved version instead.
  //    ONLY if the source course actually has voiceover. Regenerate
  //    TTS for each translated narration segment in the target language, using
  //    the SAME voice settings as the source (default gender + per-segment
  //    _narrationVoices overrides, carried over). Sequential + tolerant of
  //    individual TTS failures. Audio caches under the NEW course/slide ids.
  try {
    if (await courseHasVoiceover(courseId, modules, source.language)) {
      const voiceJobs: { slideId: string; segIdx: number; text: string; gender: VoiceGender }[] = []
      for (const m of newModules) {
        for (const s of (m.slides ?? [])) {
          const c = s.content as { _narration?: unknown; _narrationVoices?: Array<'female' | 'male' | null> }
          const segs = Array.isArray(c._narration) ? (c._narration as string[]) : []
          segs.forEach((text, i) => {
            if (typeof text === 'string' && text.trim()) {
              voiceJobs.push({ slideId: s.id, segIdx: i, text, gender: c._narrationVoices?.[i] ?? defaultGender })
            }
          })
        }
      }

      console.info(`[translate] voiceover: ${voiceJobs.length} segment(s) to generate in ${targetCode}`)
      let vdone = 0
      await mapWithConcurrency(voiceJobs, CONCURRENCY, async v => {
        checkAbort()
        try {
          const b64 = await withTtsRetry(() => generateAudioB64(v.text, signal, targetCode, v.gender, 'narration'), signal)
          await saveAudio(childId, v.slideId, v.segIdx, b64, v.gender, v.text)
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') throw err
          console.error(`[translate] VOICEOVER failed (slide ${v.slideId} seg ${v.segIdx} ${v.gender}) — skipping`, {
            message: err instanceof Error ? err.message : String(err), error: err,
          })
        }
        vdone++
        onProgress({ step: 'voiceover', progress: 48 + Math.round((vdone / Math.max(1, voiceJobs.length)) * 44) })
      })
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') void deleteCourse(childId)
    throw err
  }

  onProgress({ step: 'done', progress: 100 })
  return child
}
